<template>
    <div class="h-screen flex flex-col">
        <!-- Skip Navigation Link -->
        <a href="#main-content" class="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-white focus:text-primary focus:underline">
            Skip to main content
        </a>

        <div class="w-full flex items-center gap-x-2 border-b border-gray-400 py-2 px-3">
            <button aria-label="Toggle sidebar menu" class="rounded-md hover:bg-primary-100 w-8 h-8 flex items-center justify-center" @click="() => sidebarOpen = !sidebarOpen">
                <font-awesome class="h-3.5 w-3.5 text-gray-700 text-xs" :icon="['far', 'bars']" />
            </button>
            <NuxtLink class="h-fit" :to="`/${route.params.org_slug}`" aria-label="Home">
                <nuxt-img class="h-5" src="/logo-orange.svg" alt="Boardable logo"></nuxt-img>
            </NuxtLink>
            <div class="relative" v-click-outside="() => showSwitchedDropdown = false">
                <button @click.prevent="showSwitchedDropdown = !showSwitchedDropdown" class="text-gray-700 text-sm">
                    Public Site <font-awesome :icon="['fas', 'caret-down']" />
                </button>
                <div v-show="showSwitchedDropdown" class="absolute top-10 left-0 w-52 bg-white rounded-lg border border-gray-100 drop-shadow-lg px-2 py-2">
                    <NuxtLink :to="`${config.public.boardDomain}/${route.params.org_slug}`" class="flex items-center text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full mb-2" aria-label="Board Management (opens in new tab)">
                        Board Management
                    </NuxtLink>
                    <NuxtLink v-if="hasSurveys" :to="`${config.public.surveyDomain}/${route.params.org_slug}`" class="flex items-center text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full mb-2" aria-label="Surveys (opens in new tab)">
                        Surveys
                    </NuxtLink>
                    <NuxtLink v-if="hasForms" :to="`${config.public.formDomain}/${route.params.org_slug}`" class="flex items-center text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full mb-2" aria-label="Forms (opens in new tab)">
                        Forms
                    </NuxtLink>
                    <div class="flex items-center text-gray-700 text-base px-2 py-1 rounded bg-gray-200 w-full cursor-pointer mb-2">Public Site</div>
                </div>
            </div>
            <div class="relative ml-auto flex items-center">
                <div class="flex items-center gap-3 py-1.5 px-4 rounded-lg text-black text-sm font-medium bg-gold mr-3  hidden sm:block" v-if="config.public.isSurveys && onTrial">
                    <span class="font-medium" v-text="trialDaysLeft.message + ' ' + trialDaysLeft.emphasizedText"></span> <a class="underline ml-1 hover:text-primary" :href="`${config.public.boardDomain}/${route.params.org_slug}/subscription/plans`">Upgrade now</a>
                </div>
                <div v-click-outside="() => showSettingsDropdown = false">
                    <button aria-label="User settings and organization menu" @click.prevent="() => showSettingsDropdown = !showSettingsDropdown" class="p-2 rounded-lg border border-gray-400 flex items-center gap-x-2">
                        <ClientOnly>
                            <NuxtImg v-if="orgLogo" :src="orgLogo" class="h-5" alt="Organization logo" />
                            <span v-else-if="orgName" class="text-sm font-medium text-gray-700">{{ orgName }}</span>
                        </ClientOnly>
                        <Avatar class="w-6 h-6" :url="currentUser.profile_image" />
                    </button>
                    <div v-show="showSettingsDropdown" class="absolute z-50 top-11 right-0 w-[300px] flex flex-col gap-y-2.5 bg-white rounded-lg border border-gray-100 drop-shadow-lg px-2 py-4">
                        <NuxtLink :to="`${config.public.boardDomain}/${route.params.org_slug}/user-settings/profile`" class="flex items-center text-left justify-between text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full" aria-label="Boardable user settings (opens in new tab)">
                            Boardable user settings <font-awesome :icon="['far', 'arrow-up-right-from-square']" />
                        </NuxtLink>
                        <NuxtLink :to="`${config.public.boardDomain}/${route.params.org_slug}/settings`" class="flex items-center text-left justify-between text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full" aria-label="Boardable organization settings (opens in new tab)">
                            Boardable organization settings <font-awesome :icon="['far', 'arrow-up-right-from-square']" />
                        </NuxtLink>
                        <button v-if="config.public.userwayAccount" id="accessibilityWidget" class="flex items-center text-left justify-between text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full">Accessibility</button>
                        <button @click.prevent="logout" class="flex items-center text-left justify-between text-gray-700 text-base px-2 py-1 rounded hover:bg-gray-200 w-full">Log out</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-row flex-1 overflow-hidden">
            <aside :class="['sidebar-wrapper', { 'sidebar-open': sidebarOpen }]">
                <div class="sidebar-content h-full border-r border-gray-400 w-full flex flex-col py-5 overflow-y-auto">
                    <NuxtLink
                        :to="`/${route.params.org_slug}/admin`"
                        :class="route.path === `/${route.params.org_slug}/admin` ? 'text-primary border-primary' : 'text-gray-700 border-white'"
                        class="w-full border-l-2 flex items-center text-[15px] py-1.5 hover:bg-gray-200"
                    >
                        <span class="w-12 text-center">
                            <font-awesome class="h-4 w-4" :icon="['far', 'home']" />
                        </span>
                        <span>Dashboard</span>
                    </NuxtLink>
                    <NuxtLink
                        :to="`/${route.params.org_slug}/admin/meetings`"
                        :class="route.path === `/${route.params.org_slug}/admin/meetings` ? 'text-primary border-primary' : 'text-gray-700 border-white'"
                        class="w-full border-l-2 flex items-center text-[15px] py-1.5 hover:bg-gray-200"
                    >
                        <span class="w-12 text-center">
                            <font-awesome class="h-4 w-4" :icon="['far', 'calendar']" />
                        </span>
                        <span>Meetings</span>
                    </NuxtLink>
                    <NuxtLink
                        :to="`/${route.params.org_slug}/admin/policies`"
                        :class="route.path === `/${route.params.org_slug}/admin/policies` ? 'text-primary border-primary' : 'text-gray-700 border-white'"
                        class="w-full border-l-2 flex items-center text-[15px] py-1.5 hover:bg-gray-200"
                    >

                        <span class="w-12 text-center">
                            <font-awesome class="h-4 w-4" :icon="['far', 'list-tree']" />
                        </span>
                        <span>Policies</span>
                    </NuxtLink>
                    <NuxtLink
                        :to="`/${route.params.org_slug}/admin/customize`"
                        :class="route.path === `/${route.params.org_slug}/admin/customize` ? 'text-primary border-primary' : 'text-gray-700 border-white'"
                        class="w-full border-l-2 flex items-center text-[15px] py-1.5 hover:bg-gray-200"
                    >
                        <span class="w-12 text-center">
                            <font-awesome class="h-4 w-4" :icon="['far', 'paintbrush-pencil']" />
                        </span>
                        <span>Customize site</span>
                    </NuxtLink>
                    <NuxtLink
                        :to="`/${route.params.org_slug}/admin/activity-log`"
                        :class="route.path === `/${route.params.org_slug}/admin/activity-log` ? 'text-primary border-primary' : 'text-gray-700 border-white'"
                        class="w-full border-l-2 flex items-center text-[15px] py-1.5 hover:bg-gray-200"
                    >
                        <span class="w-12 text-center">
                            <font-awesome class="h-4 w-4" :icon="['far', 'clock-rotate-left']" />
                        </span>
                        <span>Activity Log</span>
                    </NuxtLink>
                </div>
            </aside>

            <main id="main-content" class="main flex-1 bg-gray-100 p-8 h-full overflow-y-auto">
                <slot />
            </main>
        </div>

        <ModalsContainer />
    </div>
</template>
<script setup>

    import Avatar from "~/components/ui/Avatar.vue";
    import { ModalsContainer } from "vue-final-modal";
    import { orgFetch } from "~/composables/orgFetch.js";
    import { useOrg } from "~/composables/useOrg.js";

    const route = useRoute();
    const config = useRuntimeConfig();

    const showSwitchedDropdown = ref(false);
    const showSettingsDropdown = ref(false);
    const sidebarOpen = ref(true);
    const hasForms = ref(false)
    const hasSurveys = ref(false)
    const currentUser = reactive({});
    const role = ref('')

    // Get org data for display
    const { orgName, orgLogo } = useOrg();

    // Fetch current user
    onMounted(() => {
        if(!route.params.org_slug){
            return navigateTo(config.public.boardDomain + '/login', { external: true });
        }
        orgFetch(config.public.apiUrl + '/current-org-user').then((response) => {
            Object.assign(currentUser, response);
            hasForms.value = response.has_forms
            hasSurveys.value = response.has_surveys
            role.value = response.role

            if(role.value !== 'admin'){
                return navigateTo(config.public.boardDomain + '/login', { external: true });
            }
        });

    });

    const logout = async () => {
        try {
            // Get the access token and XSRF token
            const auat = useCookie('auat');
            const xsrfToken = useCookie('XSRF-TOKEN');
            const accessToken = auat.value;
            
            if (accessToken) {
                // POST to app-logout endpoint
                const formData = new FormData();
                formData.append('access_token', accessToken);
                
                let response = await fetch(`${config.public.boardDomain}/api/app-logout`, {
                    method: 'POST',
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken.value || ''
                    },
                    body: formData,
                    credentials: 'include'
                });
            }
            
            // Clear all auth cookies
            const arst = useCookie('arst');
            const uid = useCookie('uid');
            
            arst.value = null;
            auat.value = null;
            uid.value = null;
            
            // Redirect to login page
            await navigateTo(`${config.public.boardDomain}/login`, { external: true });
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, clear cookies and redirect
            // const arst = useCookie('arst');
            // const auat = useCookie('auat');
            // const uid = useCookie('uid');
            //
            // arst.value = null;
            // auat.value = null;
            // uid.value = null;
            //
            // await navigateTo(`${config.public.boardDomain}/login`, { external: true });
        }
    };

</script>

<style scoped>
.sidebar-wrapper {
    overflow: hidden;
    width: 0;
    transition: width 0.3s ease;
}

.sidebar-wrapper.sidebar-open {
    width: 220px;
}

.sidebar-content {
    min-width: 220px;
}

.main {
    flex: 1;
    transition: all 0.3s ease;
}

/* Screen reader only - visible on focus for skip link */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

.focus\:not-sr-only:focus {
    position: static;
    width: auto;
    height: auto;
    padding: inherit;
    margin: inherit;
    overflow: visible;
    clip: auto;
    white-space: normal;
}
</style>

